import { useState, useEffect, useContext, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { Paper, makeStyles } from "@material-ui/core";
import clsx from "clsx";


import { TagsContainer } from "../TagsContainer";
import TicketActionButtons from "../TicketActionButtonsCustom";
import MessageInput from "../MessageInputCustom/";
import ContactDrawer from "../ContactDrawer";
import MessagesList from "../MessagesList";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";

import { getSocket, onSocket, cleanupComponent } from "../../services/socket";
import toastError from "../../errors/toastError";
import api from "../../services/api";

import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { TicketsContext } from "../../context/Tickets/TicketsContextIndexedDB";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  extractCloudTicketMeta,
  resolveTicketChannel,
} from "../../utils/serviceWindow";

import backgroundChat from '../../assets/img/backgroundChat.png';
const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
    width: "100%",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    width: "100%",
    marginRight: -drawerWidth,
    // backgroundImage: `url(${backgroundChat})`,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: "transparent",
    borderTop: '0px'
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
  containerHeader: {
    margin: "0 8px",
    [theme.breakpoints.down("md")]: {
      margin: 0,
    },
  },

  messagesContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    height: "100%",
    overflow: "hidden",
    position: "relative",
  },

  messagesListWrapper: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  messageInputWrapper: {
    flexShrink: 0,
    position: "relative",
    zIndex: 1,
    overflow: "visible",
    backgroundColor: theme.palette.inputBackground,
    border: `1px solid ${theme.palette.borderTableInput}`,
    margin: "8px",
    borderRadius: '12px'
  },
}));

const Ticket = ({ reloadTickets }) => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const { user } = useContext(AuthContext);
  const { getCurrentTicketInfo, ticketsList, setTicketsList } = useContext(TicketsContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loadingTicket, setLoadingTicket] = useState(true);
  const [loadingTags, setLoadingTags] = useState(false);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [quickMessageModalOpen, setQuickMessageModalOpen] = useState(false);
  const [emojiModalOpen, setEmojiModalOpen] = useState(false);

  // Referencia para controlar el timeout de carga
  const loadingTimeoutRef = useRef(null);

  // Handler optimista compartido entre MessagesList y MessageInput
  const handleOptimisticMessageRef = useRef(null);
  const handleOptimisticMessage = (msg) => {
    if (handleOptimisticMessageRef.current) {
      try {
        handleOptimisticMessageRef.current(msg);
      } catch (e) {
        console.warn("handleOptimisticMessage lanzó excepción:", e);
      }
    }
  };
  const registerOptimisticHandler = (handler) => {
    handleOptimisticMessageRef.current = handler;
  };

  const mergeCloudTicketMeta = (sourceTicket) => {
    const meta = extractCloudTicketMeta(sourceTicket);
    if (!meta) return;

    setTicket((prevTicket) => ({
      ...prevTicket,
      channel: meta.channel,
      serviceWindowExpiresAt: meta.serviceWindowExpiresAt,
      lastCustomerMessageAt: meta.lastCustomerMessageAt,
    }));
  };

  const handleTicketMetaUpdate = (sourceTicket) => {
    mergeCloudTicketMeta(sourceTicket);
  };

  const fetchTicketData = async () => {
    try {
      setLoadingTicket(true);
      
      // Establecer un timeout de seguridad para la carga
      loadingTimeoutRef.current = setTimeout(() => {
        console.log(`⚠️ Timeout de carga para ticket ${ticketId}`);
        toast.error("Error al cargar el ticket. Redirigiendo a la lista de tickets...", {
          autoClose: 3000
        });
        history.push("/tickets");
      }, 15000); // Aumentado a 15 segundos para dar más tiempo
      
      // 🔄 ESTRATEGIA MEJORADA: Intentar múltiples fuentes con fallback
      let ticketData = null;
      let contactData = null;
      
      // 1. Primero intentar desde el contexto/caché
      const contextTicket = getCurrentTicketInfo();
      const cachedTicket = ticketsList.find(t => t.uuid === ticketId);
      const ticketFromContext = cachedTicket || contextTicket;
      
      if (ticketFromContext && ticketFromContext.uuid === ticketId) {
        console.log(`✅ Ticket ${ticketId} encontrado en contexto/caché`);
        ticketData = ticketFromContext;
        contactData = ticketFromContext.contact;
      } else {
        // 2. Si no está en contexto, verificar si existe en la lista general
        const ticketExists = ticketsList.some(t => t.uuid === ticketId);
        
        if (!ticketExists) {
          console.log(`🔄 Ticket ${ticketId} no encontrado en contexto, obteniendo del servidor...`);
          
          try {
            // 3. Obtener del servidor como último recurso
            const { data } = await api.get(`/tickets/u/${ticketId}`);
            
            if (data && data.uuid === ticketId) {
              console.log(`✅ Ticket ${ticketId} obtenido del servidor`);
              ticketData = data;
              contactData = data.contact;
              
              // Agregar al contexto para futuras consultas
              setTicketsList(prevTickets => {
                const exists = prevTickets.some(t => t.uuid === ticketId);
                if (!exists) {
                  return [data, ...prevTickets];
                }
                return prevTickets;
              });
            } else {
              throw new Error('Ticket no encontrado en el servidor');
            }
          } catch (serverError) {
            console.error(`❌ Error obteniendo ticket del servidor:`, serverError);
            toast.warning("El ticket solicitado no existe o fue eliminado");
            history.push("/tickets");
            return;
          }
        } else {
          // El ticket existe en la lista pero no se pudo obtener del contexto
          console.log(`⚠️ Ticket ${ticketId} existe en lista pero no en contexto actual`);
          const foundTicket = ticketsList.find(t => t.uuid === ticketId);
          if (foundTicket) {
            ticketData = foundTicket;
            contactData = foundTicket.contact;
          }
        }
      }
      
      // 4. Validar que tenemos datos válidos antes de continuar
      if (!ticketData || !ticketData.uuid) {
        console.error(`❌ No se pudo obtener datos válidos para ticket ${ticketId}`);
        toast.error("Error al cargar los datos del ticket");
        history.push("/tickets");
        return;
      }
      
      // 5. Establecer los datos en el estado
      console.log(`🎯 Estableciendo datos para ticket ${ticketId}:`, {
        hasContact: !!contactData,
        hasTicket: !!ticketData,
        ticketStatus: ticketData.status,
        ticketUserId: ticketData.userId
      });
      
      setContact(contactData || {});
      setTicket(ticketData);
      mergeCloudTicketMeta(ticketData);

      try {
        const { data: freshTicket } = await api.get(`/tickets/u/${ticketData.uuid}`);
        if (freshTicket?.uuid === ticketId) {
          setContact(freshTicket.contact || contactData || {});
          setTicket((prevTicket) => ({
            ...prevTicket,
            ...freshTicket,
            tags: freshTicket.tags ?? prevTicket.tags,
          }));
          mergeCloudTicketMeta(freshTicket);

          setTicketsList((prevTickets) =>
            prevTickets.map((t) =>
              t.uuid === ticketId ? { ...t, ...freshTicket } : t,
            ),
          );
        }
      } catch (refreshError) {
        console.warn(`⚠️ No se pudo refrescar ticket ${ticketId} desde servidor:`, refreshError);
      }
      
      // 6. Cargar etiquetas si no están disponibles
      if (!ticketData.tags) {
        try {
          setLoadingTags(true);
          const { data: ticketWithTags } = await api.get(`/tickets/u/${ticketData.uuid}`);
          
          if (ticketWithTags && ticketWithTags.tags) {
            setTicket(prevTicket => ({
              ...prevTicket,
              tags: ticketWithTags.tags
            }));

            // Actualizar también en el contexto
            setTicketsList(prevTickets => {
              return prevTickets.map(t => 
                t.uuid === ticketId ? { ...t, tags: ticketWithTags.tags } : t
              );
            });
          }
        } catch (tagsError) {
          console.warn(`⚠️ Error cargando etiquetas para ticket ${ticketId}:`, tagsError);
          // No es crítico, continuar sin etiquetas
        } finally {
          setLoadingTags(false);
        }
      }
      
      console.log(`✅ Ticket ${ticketId} cargado exitosamente`);
      
    } catch (err) {
      console.error(`❌ Error general cargando ticket ${ticketId}:`, err);
      toastError(err);
      toast.error("Error al cargar el ticket. Redirigiendo a la lista de tickets...", {
        autoClose: 3000
      });
      setTimeout(() => history.push("/tickets"), 1000);
    } finally {
      // Limpiar el timeout de seguridad
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      setLoadingTicket(false);
    }
  };

  useEffect(() => {
    if (!ticketId) return;

    fetchTicketData();
    
    // Limpiar el timeout si el componente se desmonta
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [ticketId]); // Dependencias necesarias para la sincronización

  // Efecto para manejar eventos de socket y detectar transferencias de tickets
  useEffect(() => {
    if (!ticket.id) return;

    const companyId = localStorage.getItem("companyId");
    const componentId = `Ticket-${ticket.id}`;
    getSocket(companyId);

    const handleTicketUpdate = (data) => {
      const updatedTicket = data?.ticket;
      if (!updatedTicket || updatedTicket.id !== ticket.id) return;

      const oldUserId = data.beforeUserId;
      const oldStatus = data.beforeStatus;
      const newStatus = data.ticketStatus || updatedTicket.status;

      console.log(`🔄 Ticket ${ticket.id} actualizado en vista individual:`, {
        oldUserId,
        newUserId: updatedTicket.userId,
        currentUserId: user.id,
        oldStatus,
        newStatus
      });

      // Detectar transferencias reales: el ticket estaba con el usuario actual y ahora ya no
      const wasAssignedToCurrentUser = oldUserId !== undefined && oldUserId !== null && oldUserId === user.id;
      const isNowAssignedToOtherUser = updatedTicket.userId !== user.id && updatedTicket.userId !== null;
      const wasReturnedToPending = wasAssignedToCurrentUser && updatedTicket.userId === null && newStatus === "pending";
      const wasClosedByOtherUser = newStatus === "closed" && oldStatus && oldStatus !== "closed" && oldUserId !== undefined && oldUserId !== null && oldUserId !== user.id;

      if (wasAssignedToCurrentUser && (isNowAssignedToOtherUser || wasReturnedToPending)) {
        console.log(`🚪 Ticket ${ticket.id} transferido/devuelto - cerrando vista`);
        toast.info("El ticket ha sido transferido a otro usuario. Cerrando vista...", {
          autoClose: 3000
        });
        setTimeout(() => history.push("/tickets"), 1000);
      } else if (wasClosedByOtherUser) {
        console.log(`🔒 Ticket ${ticket.id} cerrado por otro usuario - cerrando vista`);
        toast.info("El ticket ha sido cerrado. Cerrando vista...", {
          autoClose: 3000
        });
        setTimeout(() => history.push("/tickets"), 1000);
      } else {
        setTicket(prevTicket => ({
          ...prevTicket,
          ...updatedTicket,
          channel: updatedTicket.channel ?? prevTicket.channel ?? updatedTicket.whatsapp?.channel,
          serviceWindowExpiresAt:
            updatedTicket.serviceWindowExpiresAt ?? prevTicket.serviceWindowExpiresAt,
          lastCustomerMessageAt:
            updatedTicket.lastCustomerMessageAt ?? prevTicket.lastCustomerMessageAt,
        }));
        if (updatedTicket.contact) {
          setContact(updatedTicket.contact);
        }
      }
    };

    const handleTicketResolved = (data) => {
      const updatedTicket = data?.ticket;
      if (!updatedTicket || updatedTicket.id !== ticket.id) return;

      console.log(`✅ Ticket ${ticket.id} resuelto en vista individual`);

      const oldUserId = data.beforeUserId;
      const wasClosedByOtherUser = oldUserId !== undefined && oldUserId !== null && oldUserId !== user.id;

      if (wasClosedByOtherUser) {
        toast.info("El ticket ha sido cerrado. Cerrando vista...", { autoClose: 3000 });
        setTimeout(() => history.push("/tickets"), 1000);
      } else {
        setTicket(prevTicket => ({
          ...prevTicket,
          ...updatedTicket,
          channel: updatedTicket.channel ?? prevTicket.channel ?? updatedTicket.whatsapp?.channel,
          serviceWindowExpiresAt:
            updatedTicket.serviceWindowExpiresAt ?? prevTicket.serviceWindowExpiresAt,
          lastCustomerMessageAt:
            updatedTicket.lastCustomerMessageAt ?? prevTicket.lastCustomerMessageAt,
        }));
        if (updatedTicket.contact) {
          setContact(updatedTicket.contact);
        }
      }
    };

    const handleTicketRemove = (data) => {
      const removedTicketId = data?.ticket?.id || data?.ticketId;
      if (removedTicketId !== ticket.id) return;

      console.log(`🗑️ Ticket ${ticket.id} eliminado - cerrando vista`);
      toast.info("El ticket ha sido eliminado. Cerrando vista...", { autoClose: 3000 });
      setTimeout(() => history.push("/tickets"), 1000);
    };

    onSocket("ticket:update", handleTicketUpdate, componentId);
    onSocket("ticket:accepted", handleTicketUpdate, componentId);
    onSocket("ticket:reopened", handleTicketUpdate, componentId);
    onSocket("ticket:resolved", handleTicketResolved, componentId);
    onSocket("ticket:remove", handleTicketRemove, componentId);

    return () => {
      cleanupComponent(componentId);
    };
  }, [ticket.id, user.id, history]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const renderTicketInfo = () => {
    if (ticket?.id && contact?.id) {
      return (
        <TicketInfo
          contact={contact}
          ticket={ticket}
          onClick={handleDrawerOpen}
        />
      );
    }
    return null;
  };

  const renderMessagesList = () => {
    // Solo renderizar si tenemos un ticket válido con ID
    if (!ticket?.id) {
      return (
        <div className={classes.messagesContainer}>
          <div className={classes.messagesListWrapper}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%', 
              color: '#666',
              fontSize: '16px'
            }}>
              {loadingTicket ? 'Cargando ticket...' : 'Selecciona un ticket para ver los mensajes'}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={classes.messagesContainer}>
        <div className={classes.messagesListWrapper}>
          <MessagesList
            ticket={ticket}
            ticketId={ticket.id}
            isGroup={ticket.isGroup}
            quickMessageModalOpen={quickMessageModalOpen}
            emojiModalOpen={emojiModalOpen}
            onRegisterOptimisticHandler={registerOptimisticHandler}
            onTicketMetaUpdate={handleTicketMetaUpdate}
          />
        </div>
        <div className={classes.messageInputWrapper}>
          <MessageInput
            ticketId={ticket.id}
            contactId={ticket.contactId}
            whatsappId={ticket.whatsappId}
            ticketStatus={ticket.status}
            currentUser={user}
            onOptimisticMessage={handleOptimisticMessage}
            quickMessageModalOpen={quickMessageModalOpen}
            setQuickMessageModalOpen={setQuickMessageModalOpen}
            emojiModalOpen={emojiModalOpen}
            setEmojiModalOpen={setEmojiModalOpen}
            channel={resolveTicketChannel(ticket)}
            serviceWindowExpiresAt={ticket.serviceWindowExpiresAt}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <div className={classes.containerHeader}>
          <TicketHeader loading={loadingTicket}>
            {renderTicketInfo()}
            <TicketActionButtons
              ticket={ticket}
              handleDrawerOpen={handleDrawerOpen}
              reloadTickets={reloadTickets}
            />
          </TicketHeader>
          {!loadingTicket && <TagsContainer ticket={ticket} loading={loadingTags} />}
        </div>
        <ReplyMessageProvider>{renderMessagesList()}</ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loadingTicket}
        ticket={ticket}
      />
    </div>
  );
};

export default Ticket;
